import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Check } from 'lucide-react'
import toast from 'react-hot-toast'
import api from '../services/api'
import { SERVICES, STORE_MODES, servicesOf } from '../config/services'
import { t } from '../i18n'

// The services this store runs (menu & pre-orders, online store, bookings),
// up to what its plan includes. The first one is what the website opens with.
export default function ServicesPanel({ branch, businessType }) {
  const [chosen, setChosen] = useState(() => servicesOf(branch?.services, businessType))
  const [saving, setSaving] = useState(false)
  const current = servicesOf(branch?.services, businessType)
  const currentMode = branch?.store_mode || 'both'
  const [mode, setMode] = useState(currentMode)
  // No plan chosen yet: the store keeps the services it has.
  const noPlan = !branch?.plan
  const max = noPlan ? current.length : branch.plan.max_services
  const changed = chosen.join() !== current.join() || (chosen.includes('store') && mode !== currentMode)

  const toggle = id => setChosen(list => {
    if (list.includes(id)) return list.length > 1 ? list.filter(s => s !== id) : list
    if (max === 1) return [id]
    if (max && list.length >= max) { toast.error(t('Your plan includes up to {n} services.', { n: max })); return list }
    return [...list, id]
  })
  const makeMain = id => setChosen(list => [id, ...list.filter(s => s !== id)])

  const save = async () => {
    setSaving(true)
    try {
      await api.put('/api/restaurants/current/services', { services: chosen, storeMode: mode })
      toast.success(t('Services updated'))
      // The admin menu and website are built from the services: reload them.
      window.location.reload()
    } catch (error) {
      toast.error(t(error.response?.data?.error || 'Could not update your services'))
      setSaving(false)
    }
  }

  return (
    <div className="card">
      <div className="card-body space-y-5">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{t('Your services')}</h3>
          <p className="text-sm text-gray-600 mt-1">
            {noPlan ? t('Choose a plan in Billing to add more services.') : max === 1 ? t('Your plan includes one service.') : max ? t('Your plan includes up to {n} services, all in the same store.', { n: max }) : t('Your plan includes every service, all in the same store.')}
            {' '}{branch?.plan?.name && <span className="text-gray-500">({branch.plan.name})</span>}
          </p>
        </div>
        <div className="grid gap-3 md:grid-cols-3">
          {SERVICES.map(service => {
            const on = chosen.includes(service.id)
            const main = chosen[0] === service.id
            return (
              <div key={service.id} className={`rounded-xl border p-4 ${on ? 'border-blue-600 bg-blue-50/60' : 'border-gray-200'}`}>
                <button type="button" onClick={() => toggle(service.id)} aria-pressed={on} className="flex w-full items-start gap-3 text-start">
                  <span className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded border ${on ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300 bg-white'}`}>{on && <Check className="h-3.5 w-3.5" />}</span>
                  <span>
                    <span className="flex items-center gap-2 font-medium text-gray-900"><service.icon className="h-4 w-4 text-blue-700" />{t(service.title)}</span>
                    <span className="mt-1 block text-xs text-gray-600">{t(service.desc)}</span>
                  </span>
                </button>
                {on && service.id === 'store' && (
                  <div className="mt-3 space-y-1.5 ps-8" role="radiogroup" aria-label={t('How do you sell?')}>
                    {STORE_MODES.map(m => (
                      <label key={m.value} className="flex cursor-pointer items-start gap-2 text-xs text-gray-700">
                        <input type="radio" name="store-mode" className="mt-0.5" checked={mode === m.value} onChange={() => setMode(m.value)} />
                        <span><span className="font-medium text-gray-900">{t(m.label)}</span></span>
                      </label>
                    ))}
                  </div>
                )}
                {on && chosen.length > 1 && (
                  <p className="mt-3 ps-8 text-xs">
                    {main ? <span className="font-medium text-blue-700">{t('Opens first on your website')}</span>
                      : <button type="button" onClick={() => makeMain(service.id)} className="text-blue-700 hover:underline">{t('Open this first on my website')}</button>}
                  </p>
                )}
              </div>
            )
          })}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          {max ? <Link to="/admin/billing" className="text-sm text-blue-700 hover:underline">{t('Need more services? Change your plan')}</Link> : <span />}
          <button type="button" onClick={save} disabled={!changed || saving} className="btn-primary disabled:opacity-50">{saving ? t('Saving...') : t('Save services')}</button>
        </div>
      </div>
    </div>
  )
}
